import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { PageTitle } from "@volca/ui/components/ui/page-components";
import { Separator } from "@volca/ui/components/ui/separator";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatDateRange } from "@/core/utils/date";
import { ApprovalCoverageControl } from "@/payroll/approvals/approval-coverage-control";
import type {
  ApprovalChangeHandler,
  ApprovalQueryState,
  PendingApproval,
} from "@/payroll/approvals/types";
import type { PayPeriodLockTarget } from "@/payroll/pay-periods/pay-period-lock";
import { PayPeriodLockedTitleMark } from "@/payroll/pay-periods/pay-period-locked";
import { RelativeTime } from "@/payroll/pay-periods/relative-time";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { PayBreakdownHeaderActions } from "./pay-breakdown-header-actions";
import { PayBreakdownPeriodNav } from "./pay-breakdown-period-nav";
import { getPayBreakdownPath } from "./routes";
import {
  PAY_BREAKDOWN_CALCULATION_STATE,
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
} from "./types";
import type {
  PayBreakdownCalculationState,
  PayBreakdownActiveRun,
  PayBreakdownCommentsAction,
  PayBreakdownDownloadAction,
  PayBreakdownManualEditsAction,
  PayBreakdownNextPeriodNoteAction,
  PayBreakdownOutput,
  PayBreakdownPeriod,
  PayBreakdownRerunAction,
} from "./types";

interface PayBreakdownHeaderProps {
  selectedPeriod: PayBreakdownPeriod | undefined;
  visibleRunAt: string | null | undefined;
  activeRun?: PayBreakdownActiveRun | null;
  hasFailedRun?: boolean;
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  approvalQueryState?: ApprovalQueryState;
  // Omitted for read-only surfaces: the coverage readout still renders, its
  // marks just aren't settable.
  approvalActions?: {
    pending: PendingApproval | null;
    onChange: ApprovalChangeHandler;
  };
  calculationState: PayBreakdownCalculationState;
  output: PayBreakdownOutput;
  // The XLSX export, offered from the More actions menu.
  aiWorkbookDownload: PayBreakdownDownloadAction;
  onOpenExplanationChat: () => void;
  // The section sub-tab bar, rendered snug BELOW the period header.
  tabs?: ReactNode;
  sectionActions?: ReactNode;
  nextPeriodNote?: PayBreakdownNextPeriodNoteAction;
  rosterWarning?: ReactNode;
  // Offered in the trailing overflow menu, and only to a customer with
  // whole-payroll authority on a period that is not already locked.
  lock?: PayPeriodLockTarget;
  hideDefaultActions?: boolean;
  showExplanationChatInMenu?: boolean;
  // Entry point for the manual-edits sidebar. It rides on the header while a
  // rerun has left edits awaiting review — the count is what makes it worth the
  // space — and otherwise folds into the More actions menu.
  manualEdits?: PayBreakdownManualEditsAction;
  // Entry point for the comments sidebar, placed the same way.
  comments?: PayBreakdownCommentsAction;
  rerunAction: PayBreakdownRerunAction;
  // Asks every approver on this workbook to sign off. Renders nothing for
  // businesses that have not configured sheet approvals.
  approvalRequest?: ReactNode;
  periodPicker: {
    periods: PayBreakdownPeriod[];
    calendarPeriods: PayBreakdownPeriod[];
    selectedPeriodStart: string | undefined;
    isLoading: boolean;
  };
}

export const PayBreakdownHeader = ({
  selectedPeriod,
  visibleRunAt,
  activeRun,
  hasFailedRun = false,
  approvalStatus,
  approvalQueryState,
  approvalActions,
  calculationState,
  output,
  aiWorkbookDownload,
  onOpenExplanationChat,
  tabs,
  sectionActions,
  nextPeriodNote,
  rosterWarning,
  lock,
  hideDefaultActions = false,
  showExplanationChatInMenu = false,
  manualEdits,
  comments,
  rerunAction,
  approvalRequest,
  periodPicker,
}: PayBreakdownHeaderProps) => {
  const isLocked = selectedPeriod?.is_locked ?? false;
  const navigate = useNavigate();

  const periodTitle = selectedPeriod
    ? formatDateRange(selectedPeriod.period_start, selectedPeriod.period_end)
    : "Payroll";
  const hasApprovalUnits = (approvalStatus?.access_keys.length ?? 0) > 0;
  const activeRunAt = activeRun?.at;
  const showActiveRun =
    !!activeRunAt && calculationState === PAY_BREAKDOWN_CALCULATION_STATE.rerun;

  // The workbook actions — everything that acts on a generated Grid. A section
  // with its own actions, such as PayPacks or PushPay, replaces them wholesale.
  const hasAiWorkbook = output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript;
  const showWorkbookActions = !hideDefaultActions && hasAiWorkbook;
  return (
    <Stack className="w-full" align="start" gap={2}>
      {/* PageHeader's row (title and a line of context on the left, actions on
          the right), hand-built only because the context line holds controls
          rather than text. */}
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={4}
        className="w-full flex-wrap gap-y-2"
      >
        <Stack align="start" gap={1} className="min-w-0 flex-1 basis-80">
          {/* The period, and beside it the control that changes it: what you're
              looking at and how to move to another, together. */}
          <Stack
            direction="row"
            align="center"
            gap={2}
            className="min-w-0 flex-wrap gap-x-4"
          >
            <Stack direction="row" align="center" gap={2} className="min-w-0">
              <PageTitle>{periodTitle}</PageTitle>
              {isLocked && <PayPeriodLockedTitleMark />}
            </Stack>
            <PayBreakdownPeriodNav
              periods={periodPicker.periods}
              calendarPeriods={periodPicker.calendarPeriods}
              selectedPeriodStart={periodPicker.selectedPeriodStart}
              onSelect={(nextPeriodStart) =>
                navigate(getPayBreakdownPath(nextPeriodStart))
              }
              isLoading={periodPicker.isLoading}
            />
          </Stack>
          {/* Run recency and sign-off coverage are both "state of this period"
              — one line of quiet context under the title, not stacked boxes. */}
          <Stack direction="row" align="center" gap={2} className="flex-wrap">
            {visibleRunAt && (
              <RelativeTime prefix="Calculated" timestamp={visibleRunAt} />
            )}
            {rosterWarning}
            {showActiveRun && (
              <Stack direction="row" align="center" gap={1}>
                <Spinner
                  layout="centered"
                  role="status"
                  aria-label={
                    activeRun?.isQueued
                      ? "Payroll calculation queued"
                      : "Payroll calculation in progress"
                  }
                  size="sm"
                  tone="secondary"
                  className="h-auto w-auto"
                />
                <RelativeTime
                  prefix={
                    activeRun?.isQueued ? "Rerun queued" : "Rerunning. Started"
                  }
                  timestamp={activeRunAt}
                />
                <Text variant="caption" tone="muted">
                  Showing the previous result.
                </Text>
              </Stack>
            )}
            {hasFailedRun && (
              <Stack direction="row" align="center" gap={1} role="alert">
                <WarningCircleIcon
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-danger"
                />
                <Text variant="caption" tone="destructive">
                  Latest run failed. Try again or contact support.
                </Text>
              </Stack>
            )}
            {visibleRunAt && hasApprovalUnits && (
              <Separator
                orientation="vertical"
                className="h-3 data-vertical:self-center"
              />
            )}
            <ApprovalCoverageControl
              approvalStatus={approvalStatus}
              queryState={approvalQueryState}
              pendingApproval={approvalActions?.pending}
              onApprovalChange={approvalActions?.onChange}
            />
          </Stack>
        </Stack>

        {/* The actions that operate on the period, with everything
            occasional folded into the trailing overflow menu. */}
        <Stack
          direction="row"
          align="center"
          justify="end"
          gap={2}
          // Phones: the groups flow from the left on as few rows as fit.
          className="w-full flex-wrap gap-y-2 max-sm:justify-start lg:w-auto lg:shrink-0"
        >
          {sectionActions}
          <PayBreakdownHeaderActions
            nextPeriodNote={nextPeriodNote}
            explanationChat={
              hasAiWorkbook &&
              (showWorkbookActions || showExplanationChatInMenu)
                ? {
                    onOpen: onOpenExplanationChat,
                    placement: showExplanationChatInMenu ? "menu" : "button",
                  }
                : undefined
            }
            workbook={
              showWorkbookActions
                ? {
                    download: aiWorkbookDownload,
                    manualEdits,
                    comments,
                    approvalRequest,
                  }
                : undefined
            }
            rerun={
              hideDefaultActions
                ? undefined
                : { period: selectedPeriod, action: rerunAction }
            }
            lock={lock}
            isLocked={isLocked}
          />
        </Stack>
      </Stack>

      {tabs}
    </Stack>
  );
};
